import { z as DocContext, p as jsxRuntimeExports } from "./doenetml-NmXDuMHr.js";
import React__default, { useContext, useRef } from "react";
import { s as syncLayer, r as removeJXGEventHandlers, j as jxg, g as syncVisPropValues, e as syncLineStrokeStyle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, c as computeLabelMaskCssStyle, d as attachLabelHoverHighlight } from "./JSXGraphRenderer-CoUOmMnA.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-iiMuPv8N.js";
import { BoardContext, VERTEX_LAYER_OFFSET, LINE_LAYER_OFFSET } from "./graph-CRde30im.js";
import { u as useSourceNavigation, a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-9gNGzIOR.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor, d as resolveHandleColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { a as buildLineLikeAttributes, c as buildDragHandleAttributes } from "./buildGraphicalAttributes-0RNcbd2S.js";
const polyline = React__default.memo(function Polyline(props) {
  let { componentIdx, id, SVs, actions, sourceOfUpdate, callAction } = useDoenetRenderer(props);
  Polyline.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  let polylineJXG = useRef(null);
  let pointsJXG = useRef(null);
  let pointCoords = useRef(null);
  const dragState = usePointerDragState();
  let previousNumVertices = useRef(null);
  let jsxPointAttributes = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  const {
    lastPositionFromCore: lastPositionsFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalVertices);
  let verticesFixed = useRef(false);
  let vertexIndicesDraggable = useRef([]);
  verticesFixed.current = !SVs.verticesDraggable || SVs.fixed || SVs.fixLocation;
  vertexIndicesDraggable.current = SVs.vertexIndicesDraggable;
  const { darkMode } = useContext(DocContext) || {};
  const sourceNavigation = useSourceNavigation(id);
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: polylineJXG,
    destroy: () => deletePolylineJXG()
  });
  function createPolylineJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalVertices.length !== SVs.numVertices || SVs.numericalVertices.some((x2) => x2.length !== 2)) {
      return null;
    }
    let validCoords = true;
    for (let coords of SVs.numericalVertices) {
      if (!Number.isFinite(coords[0])) {
        validCoords = false;
      }
      if (!Number.isFinite(coords[1])) {
        validCoords = false;
      }
    }
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    let jsxPolylineAttributes = {
      ...buildLineLikeAttributes({
        SVs,
        layerOffset: LINE_LAYER_OFFSET,
        fixed: fixed.current,
        fixLocation: fixLocation.current,
        darkMode
      }),
      visible: !SVs.hidden && validCoords,
      lineCap: "butt"
    };
    jsxPointAttributes.current = buildDragHandleAttributes({
      elementAttributes: jsxPolylineAttributes,
      layer: 10 * SVs.layer + VERTEX_LAYER_OFFSET,
      darkMode,
      showInfoBox: SVs.showCoordsWhenDragging
    });
    if (verticesFixed.current || SVs.hidden || !validCoords) {
      jsxPointAttributes.current.visible = false;
    }
    jsxPolylineAttributes.label = {
      highlight: false,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      highlightStrokeOpacity: 1
    };
    if (SVs.labelHasLatex) {
      jsxPolylineAttributes.label.useMathJax = true;
    }
    if (SVs.applyStyleToLabel) {
      jsxPolylineAttributes.label.strokeColor = lineColor;
    } else {
      jsxPolylineAttributes.label.strokeColor = "var(--canvasText)";
    }
    pointsJXG.current = [];
    for (let i = 0; i < SVs.numVertices; i++) {
      let pointAttributes = { ...jsxPointAttributes.current };
      if (!vertexIndicesDraggable.current.includes(i)) {
        pointAttributes.visible = false;
      }
      pointsJXG.current.push(
        board.create(
          "point",
          [...SVs.numericalVertices[i]],
          pointAttributes
        )
      );
    }
    let x = [];
    let y = [];
    SVs.numericalVertices.forEach((z) => {
      x.push(z[0]);
      y.push(z[1]);
    });
    let newPolylineJXG = board.create(
      "curve",
      [x, y],
      jsxPolylineAttributes
    );
    newPolylineJXG.isDraggable = !fixLocation.current;
    attachLabelHoverHighlight({
      hoverTargetJXG: newPolylineJXG,
      getLabelJXG: () => polylineJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    for (let i = 0; i < SVs.numVertices; i++) {
      attachVertexDragHandlers(pointsJXG.current[i], i);
    }
    attachPolylineBodyDragHandlers(newPolylineJXG);
    newPolylineJXG.on("over", () => {
      highlightVertices();
    });
    newPolylineJXG.on("out", () => {
      unHighlightVertices();
    });
    previousNumVertices.current = SVs.numVertices;
    return newPolylineJXG;
  }
  function attachPolylineBodyDragHandlers(polyline2) {
    attachLineFamilyDragHandlers({
      sourceNavigation,
      jxg: polyline2,
      tag: -1,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      participatesInDownTag: false,
      suppressClickWhenDownOnOtherTag: true,
      suppressFocusOnDownWhenDownOnOtherTag: true,
      actions: {
        move: actions.movePolyline,
        focus: actions.polylineFocused,
        click: actions.polylineClicked
      },
      snapshot: () => polyline2.points.map((x) => [...x.scrCoords]),
      buildTransientMoveArgs: (e, snap) => {
        if (!polylineJXG.current || !board) {
          return null;
        }
        polyline2.updateTransformMatrix?.();
        let shiftX = polyline2.transformMat[1][0];
        let shiftY = polyline2.transformMat[2][0];
        const next = [];
        if (e.type === "pointermove" && dragState.pointerAtDown.current && snap) {
          for (let j = 0; j < polyline2.points.length; j++) {
            next.push(
              pointerEventToUserCoords(
                e,
                dragState.pointerAtDown.current,
                snap[j],
                board
              )
            );
          }
        } else {
          for (let j = 0; j < polyline2.points.length; j++) {
            next.push([
              polyline2.dataX[j] + shiftX,
              polyline2.dataY[j] + shiftY
            ]);
          }
        }
        pointCoords.current = next;
        return {
          pointCoords: next,
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => pointCoords.current ? { pointCoords: pointCoords.current } : null,
      onDragApplied: () => {
        if (!polylineJXG.current || !pointsJXG.current || dragCoordination.draggedTag.current !== -1) {
          return;
        }
        let shiftX = polyline2.transformMat[1][0];
        let shiftY = polyline2.transformMat[2][0];
        for (let j = 0; j < SVs.numVertices; j++) {
          pointsJXG.current[j].coords.setCoordinates(
            jxg.COORDS_BY_USER,
            [...lastPositionsFromCore.current[j]]
          );
          polyline2.dataX[j] = lastPositionsFromCore.current[j][0] - shiftX;
          polyline2.dataY[j] = lastPositionsFromCore.current[j][1] - shiftY;
        }
      },
      onHitExtra: () => highlightVertices(),
      onKeyFocusOutExtra: () => unHighlightVertices()
    });
  }
  function attachVertexDragHandlers(vertex, i) {
    attachLineFamilyDragHandlers({
      sourceNavigation,
      jxg: vertex,
      tag: i,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      shouldDispatchFocusOnDown: () => !verticesFixed.current,
      actions: {
        move: actions.movePolyline,
        focus: actions.polylineFocused,
        click: actions.polylineClicked
      },
      snapshot: () => null,
      buildTransientMoveArgs: () => {
        const map = {};
        map[i] = [vertex.X(), vertex.Y()];
        pointCoords.current = map;
        return {
          pointCoords: map,
          transient: true,
          skippable: true,
          sourceDetails: { vertex: i }
        };
      },
      buildCommitMoveArgs: (_, variant) => {
        if (!pointCoords.current) {
          return null;
        }
        if (variant === "up") {
          return {
            pointCoords: pointCoords.current,
            sourceDetails: { vertex: i }
          };
        }
        return {
          pointCoords: pointCoords.current,
          sourceInformation: { vertex: i }
        };
      },
      onDragApplied: () => {
        if (!pointsJXG.current || !board || dragCoordination.draggedTag.current !== i) {
          return;
        }
        pointsJXG.current[i].coords.setCoordinates(jxg.COORDS_BY_USER, [
          ...lastPositionsFromCore.current[i]
        ]);
        board.updateInfobox(pointsJXG.current[i]);
      },
      onHitExtra: () => highlightVertices(),
      onKeyFocusOutExtra: () => unHighlightVertices()
    });
  }
  function deletePolylineJXG() {
    if (!polylineJXG.current) {
      return;
    }
    removeJXGEventHandlers(polylineJXG.current);
    board?.removeObject(polylineJXG.current);
    polylineJXG.current = null;
    if (pointsJXG.current) {
      for (let i = 0; i < SVs.numVertices; i++) {
        let point = pointsJXG.current[i];
        if (point) {
          removeJXGEventHandlers(point);
          board?.removeObject(point);
        }
      }
    }
    pointsJXG.current = null;
  }
  function highlightVertices() {
    if (!verticesFixed.current && pointsJXG.current) {
      for (let [i, point] of pointsJXG.current.entries()) {
        if (vertexIndicesDraggable.current.includes(i)) {
          point.setAttribute({ fillcolor: "var(--canvasText)" });
          point.needsUpdate = true;
          point.update();
        }
      }
    }
  }
  function unHighlightVertices() {
    if (!verticesFixed.current && pointsJXG.current) {
      for (let [i, point] of pointsJXG.current.entries()) {
        if (vertexIndicesDraggable.current.includes(i)) {
          point.setAttribute({ fillcolor: "none" });
          point.needsUpdate = true;
          point.update();
        }
      }
    }
  }
  if (board) {
    if (!polylineJXG.current) {
      polylineJXG.current = createPolylineJXG();
    } else if (SVs.numericalVertices.length !== SVs.numVertices || SVs.numericalVertices.some((x) => x.length !== 2)) {
      deletePolylineJXG();
    } else if (pointsJXG.current) {
      let validCoords = true;
      for (let coords of SVs.numericalVertices) {
        if (!Number.isFinite(coords[0])) {
          validCoords = false;
        }
        if (!Number.isFinite(coords[1])) {
          validCoords = false;
        }
      }
      polylineJXG.current.visProp.fixed = fixed.current;
      polylineJXG.current.visProp.highlight = !fixLocation.current;
      polylineJXG.current.isDraggable = !fixLocation.current;
      const handleColor = resolveHandleColor(darkMode);
      jsxPointAttributes.current.highlightFillColor = handleColor;
      let pointLayer = 10 * SVs.layer + VERTEX_LAYER_OFFSET;
      let layerChanged = syncLayer(
        polylineJXG.current,
        SVs.layer,
        LINE_LAYER_OFFSET
      );
      if (layerChanged) {
        jsxPointAttributes.current.layer = pointLayer;
      }
      if (previousNumVertices.current !== null && SVs.numVertices > previousNumVertices.current) {
        for (let i = previousNumVertices.current; i < SVs.numVertices; i++) {
          let pointAttributes = { ...jsxPointAttributes.current };
          if (!vertexIndicesDraggable.current.includes(i)) {
            pointAttributes.visible = false;
          }
          pointsJXG.current.push(
            board.create(
              "point",
              [...SVs.numericalVertices[i]],
              pointAttributes
            )
          );
          polylineJXG.current.dataX.length = SVs.numVertices;
          attachVertexDragHandlers(pointsJXG.current[i], i);
        }
      } else if (previousNumVertices.current !== null && SVs.numVertices < previousNumVertices.current) {
        for (let i = SVs.numVertices; i < previousNumVertices.current; i++) {
          let pt = pointsJXG.current.pop();
          if (pt) {
            removeJXGEventHandlers(pt);
            board?.removeObject(pt);
          }
        }
        polylineJXG.current.dataX.length = SVs.numVertices;
      }
      previousNumVertices.current = SVs.numVertices;
      polylineJXG.current.updateTransformMatrix?.();
      let shiftX = polylineJXG.current.transformMat[1][0];
      let shiftY = polylineJXG.current.transformMat[2][0];
      for (let i = 0; i < SVs.numVertices; i++) {
        pointsJXG.current[i].coords.setCoordinates(jxg.COORDS_BY_USER, [
          ...SVs.numericalVertices[i]
        ]);
        polylineJXG.current.dataX[i] = SVs.numericalVertices[i][0] - shiftX;
        polylineJXG.current.dataY[i] = SVs.numericalVertices[i][1] - shiftY;
      }
      let visible = !SVs.hidden;
      if (validCoords) {
        polylineJXG.current.visProp["visible"] = visible;
        polylineJXG.current.visPropCalc["visible"] = visible;
        let pointsVisible = visible && !verticesFixed.current;
        for (let i = 0; i < SVs.numVertices; i++) {
          let pointVisible = pointsVisible && vertexIndicesDraggable.current.includes(i);
          syncVisPropValues(pointsJXG.current[i], {
            highlightfillcolor: handleColor
          });
          pointsJXG.current[i].visProp["visible"] = pointVisible;
          pointsJXG.current[i].visPropCalc["visible"] = pointVisible;
          pointsJXG.current[i].visProp.showinfobox = SVs.showCoordsWhenDragging;
        }
      } else {
        polylineJXG.current.visProp["visible"] = false;
        polylineJXG.current.visPropCalc["visible"] = false;
        for (let i = 0; i < SVs.numVertices; i++) {
          pointsJXG.current[i].visProp["visible"] = false;
          pointsJXG.current[i].visPropCalc["visible"] = false;
        }
      }
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      syncLineStrokeStyle(polylineJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle)
      });
      polylineJXG.current.name = SVs.labelForGraph;
      if (polylineJXG.current.hasLabel && polylineJXG.current.label) {
        const label = polylineJXG.current.label;
        syncLabelStrokeColor(label, SVs.applyStyleToLabel, lineColor);
        syncLabelMaskCssStyle(label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        label.needsUpdate = true;
        label.update();
      }
      if (sourceOfUpdate.sourceInformation && componentIdx in sourceOfUpdate.sourceInformation) {
        let sourceInfo = sourceOfUpdate.sourceInformation[componentIdx];
        let vertexUpdated = typeof sourceInfo === "object" && sourceInfo ? sourceInfo.vertex : void 0;
        if (Number.isFinite(vertexUpdated)) {
          board.updateInfobox(pointsJXG.current[vertexUpdated]);
        }
      }
      polylineJXG.current.needsUpdate = true;
      polylineJXG.current.update();
      polylineJXG.current.updateVisibility?.();
      for (let i = 0; i < SVs.numVertices; i++) {
        if (layerChanged) {
          pointsJXG.current[i].setAttribute({ layer: pointLayer });
        }
        pointsJXG.current[i].needsUpdate = true;
        pointsJXG.current[i].update();
      }
      board.updateRenderer();
    }
  }
  if (SVs.hidden) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
});
export {
  polyline as default
};
